import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Formik, ErrorMessage } from 'formik'
import Alert from 'components/Alert'
import { Modal } from '../../../common/Modal'
import * as yup from 'yup'
import _ from 'lodash'

import { Button, TextInput } from '../../../../shard'
import { Color, Size } from '../../../../../const'

import { useAccountHook } from '../../../../../hooks'

function AccountEdit() {
  const [values, setValues] = useState({})
  const {
    user,
    isIdle,
    isLoading,
    isUpdating,
    errors,
    updateErrors,
    setErrors,
    setUpdateErrors,
    history,
    resultDisplay,
    setResultDisplay,
    execGetUser,
    execUpdateAccountInfo,
  } = useAccountHook()

  useEffect(() => {
    execGetUser()
  }, [execGetUser])

  useEffect(() => {
    if (_.isEmpty(values)) {
      return
    }
    execUpdateAccountInfo(values)
  }, [execUpdateAccountInfo, values])

  if (isIdle || isLoading) {
    return (
      <AccountBlock>
        <Caution>アカウント情報を取得中です...</Caution>
      </AccountBlock>
    )
  } else {
    return (
      <AccountBlock>
        {errors.length > 0 && (
          <Alert type="error" message="エラー" description={errors.join('\n')} onClose={() => setErrors([])} />
        )}
        {updateErrors.length > 0 && (
          <Alert
            type="error"
            message="エラー"
            description={updateErrors.join('\n')}
            onClose={() => setUpdateErrors([])}
          />
        )}
        <Modal
          title="変更完了"
          text="お客様情報の変更が完了しました。"
          caution="アカウント設定画面へ戻ります"
          display={resultDisplay}
          overlayOnClick={() => setResultDisplay(false)}
          buttonOnClick={() => history.push('/account')}
        />
        {_.isEmpty(user) ? (
          <AccountBlock>
            <Caution>アカウント情報がありません</Caution>
          </AccountBlock>
        ) : (
          <Formik
            initialValues={{
              lastname: user.lastname,
              firstname: user.firstname,
              lastname_kana: user.lastname_kana,
              firstname_kana: user.firstname_kana,
              phone: user.telephone ? user.telephone : '',
              mobilePhone: user.mobilephone ? user.mobilephone : '',
              addresses: user.addresses,
              emailaddress1: user.emailaddress1 || '',
            }}
            onSubmit={(values) => {
              setValues(values)
            }}
            validationSchema={yup.object().shape({
              lastname: yup.string().required('項目が未入力です'),
              firstname: yup.string().required('項目が未入力です'),
              lastname_kana: yup.string().required('項目が未入力です'),
              firstname_kana: yup.string().required('項目が未入力です'),
              emailaddress1: yup.string().required('項目が未入力です'),
              phone: yup
                .string()
                .test('mobilePhone', '電話番号もしくは携帯電話番号のどちらか一方を入力してください', function(value) {
                  return this.parent.mobilePhone || value
                })
                .matches(/^[0-9]*$/, { message: '数字を入力してください' }),
              mobilePhone: yup
                .string()
                .test('phone', '電話番号もしくは携帯電話番号のどちらか一方を入力してください', function(value) {
                  return this.parent.phone || value
                })
                .matches(/^[0-9]*$/, { message: '数字を入力してください' }),
              addresses: yup.object().shape({
                address1: yup.object().shape({
                  address1: yup.string().required('項目が未入力です'),
                  address2: yup.string().required('項目が未入力です'),
                  address3: yup.string(),
                  city: yup.string().required('項目が未入力です'),
                  state: yup.string().required('項目が未入力です'),
                  zipcode: yup
                    .string()
                    .required('項目が未入力です')
                    .matches(/^[0-9]{3}-?[0-9]{4}$/, { message: '形式に誤りがあります' }),
                }),
                address2: yup.object().shape({
                  address1: yup.string(),
                  address2: yup.string(),
                  address3: yup.string(),
                  city: yup.string(),
                  state: yup.string(),
                  zipcode: yup.string().matches(/^[0-9]{3}-?[0-9]{4}$/, { message: '形式に誤りがあります' }),
                }),
              }),
            })}
          >
            {({ values, handleChange, handleBlur, handleSubmit, dirty, isValid, setFieldValue }) => (
              <form
                onSubmit={(e) => {
                  handleSubmit(e)
                  e.preventDefault() // useEffectでstateを更新するためにcomponentの自動更新を防ぐ
                }}
              >
                <AccountArea>
                  <SeparateDiv>基本情報</SeparateDiv>
                  <MarginArea />
                  <TextArea>
                    <TextLabel>氏名</TextLabel>
                    <Text>
                      {user.lastname} {user.firstname}
                    </Text>
                  </TextArea>
                  <MarginArea />
                  <TextArea>
                    <TextLabel>フリガナ</TextLabel>
                    <Text>
                      {user.lastname_kana} {user.firstname_kana}
                    </Text>
                  </TextArea>
                  <MarginArea />
                  <TextArea>
                    <TextLabel>性別</TextLabel>
                    <Text>{user.gender}</Text>
                  </TextArea>
                  <MarginArea />
                  <TextArea>
                    <TextLabel>生年月日</TextLabel>
                    <Text>
                      {user.birthday_year}年{user.birthday_month}月{user.birthday_day}日
                    </Text>
                  </TextArea>
                  <MarginArea />

                  <SeparateDiv>連絡先</SeparateDiv>
                  <MarginArea />
                  <TextArea>
                    <TextLabel>電話番号</TextLabel>
                    <Text>
                      <TextInput
                        type="tel"
                        name="phone"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.phone}
                        ariaLabel="phone"
                        placeholder="09012345678"
                        errorMessage={<ErrorMessage name="phone" />}
                      />
                    </Text>
                  </TextArea>
                  <TextArea>
                    <TextLabel>携帯電話番号</TextLabel>
                    <Text>
                      <TextInput
                        type="tel"
                        name="mobilePhone"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.mobilePhone}
                        ariaLabel="mobilePhone"
                        placeholder="09012345678"
                        errorMessage={<ErrorMessage name="mobilePhone" />}
                      />
                    </Text>
                  </TextArea>
                  <MarginArea />

                  <SeparateDiv>住所</SeparateDiv>
                  <MarginArea />
                  <TextArea>
                    <TextLabel>郵便番号</TextLabel>
                    <Text>
                      <TextInput
                        type="text"
                        name="addresses.address1.zipcode"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.addresses.address1.zipcode}
                        ariaLabel="addresses.address1.zipcode"
                        placeholder="半角英数、ハイフンなし"
                        errorMessage={<ErrorMessage name="addresses.address1.zipcode" />}
                      />
                    </Text>
                  </TextArea>
                  <TextArea>
                    <TextLabel>都道府県</TextLabel>
                    <Text>
                      <TextInput
                        type="text"
                        name="addresses.address1.state"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.addresses.address1.state}
                        ariaLabel="addresses.address1.state"
                        placeholder="広島県"
                        errorMessage={<ErrorMessage name="addresses.address1.state" />}
                      />
                    </Text>
                  </TextArea>
                  <TextArea>
                    <TextLabel>市区町村</TextLabel>
                    <Text>
                      <TextInput
                        type="text"
                        name="addresses.address1.city"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.addresses.address1.city}
                        ariaLabel="addresses.address1.city"
                        placeholder="広島市中央区"
                        errorMessage={<ErrorMessage name="addresses.address1.city" />}
                      />
                    </Text>
                  </TextArea>
                  <TextArea>
                    <TextLabel>町域名</TextLabel>
                    <Text>
                      <TextInput
                        type="text"
                        name="addresses.address1.address1"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.addresses.address1.address1}
                        ariaLabel="addresses.address1.address1"
                        placeholder="本通り"
                        errorMessage={<ErrorMessage name="addresses.address1.address1" />}
                      />
                    </Text>
                  </TextArea>
                  <TextArea>
                    <TextLabel>番地・建物名</TextLabel>
                    <Text>
                      <TextInput
                        type="text"
                        name="addresses.address1.address2"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.addresses.address1.address2}
                        ariaLabel="addresses.address1.address2"
                        placeholder="2-10"
                        errorMessage={<ErrorMessage name="addresses.address1.address2" />}
                      />
                    </Text>
                  </TextArea>
                  <MarginArea />

                  <SeparateDiv>
                    <Left>配送先</Left>
                    <Right
                      onClick={() => {
                        setFieldValue('addresses.address2', values.addresses.address1)
                      }}
                    >
                      住所をコピー
                    </Right>
                  </SeparateDiv>
                  <MarginArea />
                  <TextArea>
                    <TextLabel>郵便番号</TextLabel>
                    <Text>
                      <TextInput
                        type="text"
                        name="addresses.address2.zipcode"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.addresses.address2.zipcode}
                        ariaLabel="addresses.address2.zipcode"
                        placeholder="半角英数、ハイフンなし"
                        errorMessage={<ErrorMessage name="addresses.address2.zipcode" />}
                      />
                    </Text>
                  </TextArea>
                  <TextArea>
                    <TextLabel>都道府県</TextLabel>
                    <Text>
                      <TextInput
                        type="text"
                        name="addresses.address2.state"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.addresses.address2.state}
                        ariaLabel="addresses.address2.state"
                        placeholder="広島県"
                        errorMessage={<ErrorMessage name="addresses.address2.state" />}
                      />
                    </Text>
                  </TextArea>
                  <TextArea>
                    <TextLabel>市区町村</TextLabel>
                    <Text>
                      <TextInput
                        type="text"
                        name="addresses.address2.city"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.addresses.address2.city}
                        ariaLabel="addresses.address2.city"
                        placeholder="広島市中央区"
                        errorMessage={<ErrorMessage name="addresses.address2.city" />}
                      />
                    </Text>
                  </TextArea>
                  <TextArea>
                    <TextLabel>町域名</TextLabel>
                    <Text>
                      <TextInput
                        type="text"
                        name="addresses.address2.address1"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.addresses.address2.address1}
                        ariaLabel="addresses.address2.address1"
                        placeholder="本通り"
                        errorMessage={<ErrorMessage name="addresses.address2.address1" />}
                      />
                    </Text>
                  </TextArea>
                  <TextArea>
                    <TextLabel>番地・建物名</TextLabel>
                    <Text>
                      <TextInput
                        type="text"
                        name="addresses.address2.address2"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.addresses.address2.address2}
                        ariaLabel="addresses.address2.address2"
                        placeholder="2-10"
                        errorMessage={<ErrorMessage name="addresses.address2.address2" />}
                      />
                    </Text>
                  </TextArea>
                  <MarginArea />
                  <SeparateDiv>メールアドレス</SeparateDiv>
                  <MarginArea />
                  <TextArea>
                    <TextLabel>メールアドレス</TextLabel>
                    <Text>
                      <TextInput
                        type="text"
                        name="emailaddress1"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.emailaddress1}
                        ariaLabel="emailaddress1"
                        placeholder="メールアドレス"
                        errorMessage={<ErrorMessage name="emailaddress1" />}
                      />
                    </Text>
                  </TextArea>
                </AccountArea>
                <ButtonWrapper>
                  <Button type={'submit'} primary disabled={!isValid || !dirty || isUpdating}>
                    保存する
                  </Button>
                </ButtonWrapper>
              </form>
            )}
          </Formik>
        )}
      </AccountBlock>
    )
  }
}

const AccountBlock = styled.div``
const Caution = styled.div`
  margin: 40px;
`
const SeparateDiv = styled.div`
  padding: 9px 21px 9px 21px;
  background-color: ${Color.BACKGROUND.MOBILE_LABEL};
  display: flex;
  justify-content: space-between;
`
const AccountArea = styled.div``
const TextArea = styled.div`
  margin: 0 20px;
`
const TextLabel = styled.div`
  color: ${Color.FONT.ANNOTATION};
  font-size: ${Size.FONT.REGULAR};
`
const Text = styled.div`
  color: ${Color.FONT.NORMAL};
  font-size: ${Size.FONT.BIG};
`
const MarginArea = styled.div`
  padding-bottom: 25px;
`
const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px 0;
`
const Left = styled.div``
const Right = styled.div`
  cursor: pointer;
  color: ${Color.FONT.LINK};
`
export default AccountEdit
